import { Modal, ButtonGroup, TitleScreen } from "components/Core"
import { HiArrowLeft, HiCheckCircle, HiArrowRight } from "react-icons/hi"
import { FaSave } from "react-icons/fa"
import { FormItem } from "components/FormEntities"
import useForm from "hooks/useForm"
import item_values from "utils/forms_default/item_values"
import schemaValidationItems from "utils/yup/items_schema"
import { fill_form_from_data, parsed_body_to_send } from "components/FormEntities/FormItem/item.config"
import { useFieldArray } from "react-hook-form"
import useItemsResources from "hooks/entities/useItemsResources"
import Swal from "sweetalert2"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import cn from "classnames"
import usePreRender from "hooks/usePreRender"

interface ICloneItemModal {
  mounted: boolean
  onClose: () => void
  itemToClone: any
  onSuccess?: (newSku: string) => void
  showCancel?: boolean
}

const CloneItemModal = ({ mounted, onClose, itemToClone, onSuccess, showCancel = false }: ICloneItemModal) => {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [createdSku, setCreatedSku] = useState("")
  const navigate = useNavigate()
  const { _color } = usePreRender()
  const { control, errors, handleSubmit, watch, reset } = useForm({
    defaultValues: item_values,
    schema: schemaValidationItems,
  })
  const { set_loading_app, put_load_items_action } = useItemsResources()
  const custom_field_form = useFieldArray({ control, name: "custom" })

  useEffect(() => {
    if (mounted && itemToClone) {
      reset({ ...fill_form_from_data(itemToClone), sku: "" })
    }
  }, [mounted, itemToClone, reset])

  const on_submit = async (data: any) => {
    try {
      set_loading_app(true)
      const body = parsed_body_to_send(data)
      let res: any = await put_load_items_action({ body })
      if (res?.data?.code === 200) {
        setCreatedSku(body.sku)
        setIsSuccessModalOpen(true)
      }
      set_loading_app(false)
    } catch (error: any) {
      Swal.fire({
        title: "Error",
        text: JSON.stringify(error?.response?.data?.message),
        icon: "error",
      })
      set_loading_app(false)
    }
  }

  return (
    <Modal mounted={mounted} className="max-w-7xl" localIconClose={true}>
      <div className="w-full h-full flex flex-col p-6 lg:p-10 overflow-hidden max-h-[90vh]">
        {/* Header */}
        <div className="w-full flex justify-between items-start mb-8">
          <div className="flex flex-col">
            <span className="text-[12px] font-bold text-gray-400 uppercase tracking-widest mb-1">Nuevo Artículo por Clonación</span>
            <TitleScreen title="Clonar artículo" />
            <p className="text-gray-500 mt-1">Completa el SKU y ajusta los detalles para el nuevo artículo.</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-all text-gray-500"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content - Scrollable Form */}
        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
          <form className="w-full flex flex-col" onSubmit={handleSubmit(on_submit)}>
            <div className="w-full flex flex-row items-center justify-end mb-8 sticky top-0 z-10 bg-white/80 backdrop-blur-sm py-2">
              <ButtonGroup 
                saveIcon={<FaSave className="text-xl" />}
                cancelIcon={<HiArrowLeft className="text-lg" />}
                showCancel={showCancel}
              />
            </div>
            
            <div className="w-full">
              <FormItem 
                control={control} 
                errors={errors} 
                watch={watch} 
                custom_field_form={custom_field_form} 
                isModal={true}
              />
            </div>
          </form>
        </div>
      </div>

      {/* Success Confirmation Modal */}
      <Modal mounted={isSuccessModalOpen} className="!w-[450px] !max-w-[90vw] overflow-hidden">
        <div className="w-full bg-white p-8 flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-6">
            <HiCheckCircle className="text-4xl text-green-500" />
          </div>
          <h3 className="text-[22px] font-black text-gray-800 leading-tight mb-2">Artículo Clonado</h3>
          <p className="text-gray-500 mb-8 font-medium">El artículo <span className="text-[#F9004D] font-bold">#{createdSku}</span> ha sido creado correctamente.</p>
          
          <div className="w-full flex flex-col gap-3">
            <button 
              onClick={() => {
                setIsSuccessModalOpen(false)
                onClose()
                if (onSuccess) onSuccess(createdSku)
              }}
              className="w-full bg-gray-100 hover:bg-gray-200 py-3.5 rounded-xl text-[15px] font-bold text-gray-600 transition-all"
            >
              Aceptar
            </button>
            <button 
              onClick={() => {
                setIsSuccessModalOpen(false)
                onClose()
                navigate(`/items/${createdSku}`)
              }}
              className={cn("w-full text-white py-3.5 rounded-xl text-[15px] font-bold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all", `bg_${_color}`)}
            >
              Ir al artículo nuevo
              <HiArrowRight className="text-lg" />
            </button>
          </div>
        </div>
      </Modal>
    </Modal>
  )
}

export default CloneItemModal
